import { PlusIcon } from '@heroicons/react/24/solid'
import { useEffect, useState } from 'react'
import { useImageCropContext } from '../shared/image-crop/ImageCropProvider'
import LogoModal from '../shared/image-crop/LogoModal'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { readFile } from 'lib/cropImage'

function formatFileSize(bytes: number): string {
  const sizes = [`Bytes`, `KB`, `MB`, `GB`, `TB`]
  if (bytes === 0) return `0 Byte`
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`
}

export function LocationLogoUploader({ locationId }: { locationId: string }) {
  const [preview, setPreview] = useState<string>()
  const [fileName, setFileName] = useState<string>()
  const [fileSize, setFileSize] = useState<string>()
  const [lastModified, setLastModified] = useState<string>()
  const { getProcessedImage, setImage, resetStates } = useImageCropContext()
  const [isDone, setIsDone] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)

  // The preview is also updated after upload, so it is local synchronization state.
  // oxlint-disable-next-line react/no-deriving-state-in-effects
  useEffect(() => {
    // Intentional synchronization: upload completion also updates this preview state.
    // oxlint-disable-next-line react/no-deriving-state-in-effects
    setPreview(`/api/locations/${locationId}/scheduling/logo`)
  }, [locationId])

  const onDone = () => {
    setIsDone(true)
  }

  const handleDone = async () => {
    const profilePicture = await getProcessedImage()
    if (!profilePicture) return
    setPreview(window.URL.createObjectURL(profilePicture))
    setUploadLoading(true)

    const arrayBuffer = await profilePicture.arrayBuffer()
    const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)))

    try {
      const res = await fetch(`/api/locations/${locationId}/scheduling/logo`, {
        method: `POST`,
        credentials: `include`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({
          base64,
          fileName: profilePicture.name || `logo.png`,
          contentType: profilePicture.type || `image/png`,
        }),
      })
      const data = await res.json()
      if (data.fileUrl) {
        notifySuccess(`Logo updated`)
        setPreview(data.fileUrl)
        setFileName(data.name)
        setFileSize(formatFileSize(data.size ?? 0))
        setLastModified(
          new Date().toLocaleDateString(`en-US`, {
            day: `numeric`,
            month: `long`,
            year: `numeric`,
          }),
        )
      } else {
        notifyError(`Error updating logo`)
      }
    } catch (e) {
      logError(`Error uploading scheduling logo`, e)
      notifyError(`Error updating logo`)
    }

    setUploadLoading(false)
    resetStates()
    closeModal()
  }

  const handleFileChange = async ({ target: { files } }) => {
    const file = files && files[0]
    const imageDataUrl = (await readFile(file)) as string
    setImage(imageDataUrl)
    openLogoModal()
  }

  const handleDelete = async () => {
    try {
      await fetch(`/api/locations/${locationId}/scheduling/logo`, {
        method: `DELETE`,
        credentials: `include`,
      })
      setPreview(undefined)
      closeModal()
      notifySuccess(`Logo deleted`)
    } catch (e) {
      logError(`Error deleting scheduling logo`, e)
      notifyError(`Error deleting logo`)
    }
  }

  const onLogoClick = (e) => {
    if (preview) {
      e.preventDefault()
      setImage(preview)
      openLogoModal()
    }
  }

  const openLogoModal = () => {
    showModal(<LogoModal handleDone={() => onDone()} handleDelete={handleDelete} shape="rect" aspectRatio={3} />, {
      title: `Edit Logo`,
      isFullScreen: false,
      is80Percent: false,
      removePadding: true,
      useRadix: true,
    })
  }

  useEffect(() => {
    if (isDone) {
      bg(async () => {
        await handleDone()
        setIsDone(false)
      })
    }
  }, [isDone])

  return (
    <div className="group flex items-center justify-center">
      <input type="file" onChange={handleFileChange} className="hidden" id={`picInput` + locationId} accept="image/*" />
      <div className="relative flex w-full flex-row items-center justify-start gap-6 rounded-lg border border-dashed border-default bg-surface-subtle p-6">
        {!preview ? (
          <label
            htmlFor={`picInput` + locationId}
            className="flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-surface-muted p-2"
            onClick={onLogoClick}
          >
            <PlusIcon
              strokeWidth={1.5}
              className="rounded-full bg-surface p-1 text-body-xs text-strong shadow-elevation-2 hover:cursor-pointer"
            />
          </label>
        ) : (
          <img src={preview} height={100} width={300} className="bg-transparent object-cover" alt="" />
        )}
        <div className="flex flex-col gap-3">
          {!preview ? (
            <span className="text-ui-md text-strong">Select an image</span>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <span className="text-ui-md text-strong">{fileName}</span>
                <div className="flex gap-2 text-label-sm text-subtle">
                  <span>{fileSize}</span>
                  <span>Uploaded {lastModified}</span>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="cursor-pointer p-[2px] text-ui-sm" onClick={(e) => onLogoClick(e)}>
                  Replace
                </div>
                <div className="cursor-pointer p-[2px] text-ui-sm" onClick={() => handleDelete()}>
                  Remove
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
